import React, { useState, useEffect } from "react";
import {
  useStripe,
  useElements,
  CardElement,
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe, StripeCardElementChangeEvent } from "@stripe/stripe-js";
import { X, CreditCard, AlertCircle, CheckCircle } from "lucide-react";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Initialize Stripe by fetching publishable key from backend
let stripePromise: Promise<any> | null = null;

const getStripe = async () => {
  if (!stripePromise) {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/stripe/config`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch Stripe config: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Failed to load Stripe configuration');
      }

      if (!data.data?.publishableKey) {
        throw new Error('Stripe publishable key is missing. Please check your server configuration.');
      }

      stripePromise = loadStripe(data.data.publishableKey);
    } catch (error) {
      console.error('Failed to load Stripe config:', error);
      throw error; // Re-throw to allow caller to handle
    }
  }
  return stripePromise;
};

interface PaymentFormProps {
  clientSecret: string;
  amount: number;
  currency: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  clientSecret,
  amount,
  currency,
  onSuccess,
  onCancel,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      console.error("Stripe not loaded");
      return;
    }

    if (!cardComplete) {
      setError("Please complete your card details");
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        throw new Error("Card element not found");
      }

      // Confirm the payment
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (confirmError) {
        console.error("Payment confirmation error:", confirmError);
        setError(confirmError.message || "Payment failed. Please try again.");
        setProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        setSuccess(true);
        // Wait a moment to show success message, then call onSuccess
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        setError("Payment was not completed. Please try again.");
        setProcessing(false);
      }
    } catch (err) {
      console.error("Payment error:", err);
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred"
      );
      setProcessing(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8">
        <CheckCircle className="h-16 w-16 text-success-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Payment Successful!
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Your subscription is now active. Redirecting...
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Complete Payment
          </h3>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              ${(amount / 100).toFixed(2)}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {currency.toUpperCase()}
            </p>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg border border-gray-200 dark:border-gray-500">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <CreditCard className="inline h-4 w-4 mr-2" />
            Card Details
          </label>
          <div className="bg-white dark:bg-gray-700 p-3 rounded border border-gray-300 dark:border-gray-500">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": {
                      color: "#aab7c4",
                    },
                  },
                  invalid: {
                    color: "#9e2146",
                  },
                },
              }}
              onChange={handleCardChange}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start">
          <AlertCircle className="h-5 w-5 text-error-400 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-error-800 dark:text-error-200">
              Payment Error
            </h4>
            <p className="text-sm text-error-700 dark:text-error-300 mt-1">{error}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing || !cardComplete}
          className="px-6 py-2 text-sm font-medium text-white bg-accent-600 rounded-md hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {processing ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Processing...
            </>
          ) : (
            `Pay $${(amount / 100).toFixed(2)}`
          )}
        </button>
      </div>
    </form>
  );
};

const StripeElementsWrapper: React.FC<{
  clientSecret: string;
  amount: number;
  currency: string;
  onSuccess: () => void;
  onCancel: () => void;
}> = ({ clientSecret, amount, currency, onSuccess, onCancel }) => {
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getStripe()
      .then((promise) => {
        setStripePromise(promise);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load Stripe:', err);
        setError('Failed to load Stripe. Please contact support.');
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600" />
        <p className="mt-4 text-gray-600 dark:text-gray-400">
          Loading payment system...
        </p>
      </div>
    );
  }

  if (error || !stripePromise) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-error-500 mx-auto mb-4" />
        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Stripe Configuration Error
        </h4>
        <p className="text-gray-600 dark:text-gray-400">
          {error || 'Stripe is not configured. Please contact support.'}
        </p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentForm
        clientSecret={clientSecret}
        amount={amount}
        currency={currency}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </Elements>
  );
};

interface PaymentCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const PaymentCompletionModal: React.FC<PaymentCompletionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<{
    clientSecret: string;
    amount: number;
    currency: string;
  } | null>(null);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchPaymentStatus();
      // Initialize Stripe when modal opens
      getStripe()
        .then(() => {
          setStripeLoaded(true);
          setStripeError(null);
        })
        .catch((err) => {
          console.error('Failed to initialize Stripe:', err);
          setStripeError(err instanceof Error ? err.message : 'Failed to load Stripe');
          setStripeLoaded(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchPaymentStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/subscriptions/retry-payment`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to retrieve payment information");
      }

      if (data.success && data.data.clientSecret) {
        setPaymentData({
          clientSecret: data.data.clientSecret,
          amount: data.data.amount || 0,
          currency: data.data.currency || "usd",
        });
      } else if (data.data.paymentSucceeded) {
        // Payment already succeeded
        onSuccess();
      } else {
        throw new Error("No payment information available");
      }
    } catch (err) {
      console.error("Fetch payment status error:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load payment information. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    fetchPaymentStatus();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="payment-modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="relative inline-block align-middle bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:w-full sm:max-w-lg">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 px-6 pt-5 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3
                id="payment-modal-title"
                className="text-lg font-semibold text-gray-900 dark:text-gray-100"
              >
                Complete Your Subscription Payment
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                aria-label="Close modal"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-gray-800 px-6 py-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600" />
                <p className="mt-4 text-gray-600 dark:text-gray-400">
                  Loading payment information...
                </p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <AlertCircle className="h-12 w-12 text-error-500 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Unable to Load Payment
                </h4>
                <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
                <div className="flex justify-center space-x-3">
                  <button
                    onClick={handleRetry}
                    className="px-4 py-2 bg-accent-600 text-white rounded-md hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : paymentData ? (
              stripeLoaded && !stripeError ? (
                <StripeElementsWrapper
                  clientSecret={paymentData.clientSecret}
                  amount={paymentData.amount}
                  currency={paymentData.currency}
                  onSuccess={onSuccess}
                  onCancel={onClose}
                />
              ) : stripeError ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-12 w-12 text-error-500 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Stripe Configuration Error
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">{stripeError}</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-accent-600" />
                  <p className="mt-4 text-gray-600 dark:text-gray-400">
                    Loading payment system...
                  </p>
                </div>
              )
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
